/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useEmailConfigSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling email config form submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { createEmailConfig, updateEmailConfig, deleteEmailConfig } from '../api/emailSettingsApi';
import { AUTH_CREDENTIALS_REQUIRED, PLACEHOLDER_PASSWORD, DEFAULT_FORM_VALUES } from './useEmailConfigForm';
import type { EmailConfigFormData } from './useEmailConfigForm';
import type { EmailConfig } from '../types';

export function useEmailConfigSubmit(
  id: string | undefined,
  mode: 'create' | 'edit',
  onSuccess?: (config: EmailConfigFormData) => void
) {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';

  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = useCallback(
    async (formValues: EmailConfigFormData, hasChanges: boolean, setError?: UseFormSetError<EmailConfigFormData>): Promise<EmailConfig | undefined> => {
      // hasChanges validation removed

      try {
        setLoading(true);

        const passwordValue = formValues.password ?? '';
        const trimmedPassword = passwordValue.trim();
        const requiresCredentials = AUTH_CREDENTIALS_REQUIRED.includes(formValues.authType);

        if (mode === 'create' && !trimmedPassword && requiresCredentials) {
          const errorMsg = t('emailSettings.validation.passwordRequired');
          if (setError) {
            setError('password', { type: 'manual', message: errorMsg });
          } else {
            throw new Error(errorMsg);
          }
          return;
        }

        if (
          mode === 'edit' &&
          passwordValue &&
          passwordValue !== PLACEHOLDER_PASSWORD &&
          !trimmedPassword &&
          requiresCredentials
        ) {
          const errorMsg = t('emailSettings.validation.passwordRequired');
          if (setError) {
            setError('password', { type: 'manual', message: errorMsg });
          } else {
            throw new Error(errorMsg);
          }
          return;
        }

        if (requiresCredentials && !formValues.username?.trim()) {
          const errorMsg = t('emailSettings.validation.usernameRequired');
          if (setError) {
            setError('username', { type: 'manual', message: errorMsg });
          } else {
            throw new Error(errorMsg);
          }
          return;
        }

        const ensureNumber = (value: number | undefined, fallback: number) =>
          Number.isFinite(value) ? Number(value) : fallback;

        const payload: Record<string, unknown> = {
          name: formValues.name.trim(),
          host: formValues.host.trim(),
          port: ensureNumber(formValues.port, DEFAULT_FORM_VALUES.port),
          secure: Boolean(formValues.secure),
          tls: Boolean(formValues.tls),
          authType: formValues.authType,
          username: formValues.username?.trim() || undefined,
          password: trimmedPassword,
          fromEmail: formValues.fromEmail.trim(),
          fromName: formValues.fromName.trim(),
          replyTo: formValues.replyTo?.trim() || '',
          connectionTimeout: ensureNumber(
            formValues.connectionTimeout,
            DEFAULT_FORM_VALUES.connectionTimeout
          ),
          greetingTimeout: ensureNumber(
            formValues.greetingTimeout,
            DEFAULT_FORM_VALUES.greetingTimeout
          ),
          socketTimeout: ensureNumber(
            formValues.socketTimeout,
            DEFAULT_FORM_VALUES.socketTimeout
          ),
          isActive: Boolean(formValues.isActive),
          isDefault: Boolean(formValues.isDefault),
        };

        if (mode === 'edit' && (!passwordValue || passwordValue === PLACEHOLDER_PASSWORD)) {
          delete payload.password;
        }

        let result: EmailConfig;
        if (mode === 'create') {
          result = await createEmailConfig(payload, tenantSlug, user?.id ? Number(user.id) : undefined);
          showSuccess(t('emailSettings.messages.createSuccess'));
        } else {
          if (!id || id === 'new') {
            throw new Error('Invalid ID for update');
          }
          result = await updateEmailConfig(id, payload, tenantSlug, user?.id ? Number(user.id) : undefined);
          showSuccess(t('emailSettings.messages.updateSuccess'));

          const maskedFormValues: EmailConfigFormData = {
            ...formValues,
            name: payload.name as string,
            host: payload.host as string,
            fromEmail: payload.fromEmail as string,
            fromName: payload.fromName as string,
            replyTo: (payload.replyTo as string) || '',
            username: (payload.username as string) || '',
            port: payload.port as number,
            connectionTimeout: payload.connectionTimeout as number,
            greetingTimeout: payload.greetingTimeout as number,
            socketTimeout: payload.socketTimeout as number,
            password: trimmedPassword === '' ? '' : PLACEHOLDER_PASSWORD,
          };

          onSuccess?.(maskedFormValues);
        }
        
        return result;
      } catch (error) {
        showError(error?.message || t('emailSettings.messages.saveError'));
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [id, mode, tenantSlug, user?.id, showSuccess, showError, showInfo, t, onSuccess]
  );

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteEmailConfig(id, tenantSlug, user?.id ? Number(user.id) : undefined);
      showSuccess(t('emailSettings.messages.deleteSuccess'));
    } catch (error) {
      showError(error?.message || t('emailSettings.messages.deleteError') || 'Failed to delete email configuration');
    } finally {
      setIsDeleting(false);
    }
  }, [id, tenantSlug, user?.id, showSuccess, showError, t]);

  return {
    loading,
    isDeleting,
    handleSubmit,
    handleDelete,
  };
}

